import { Badge, Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { parseFiltSearchParam } from "@/modules/forms/domain/lib/answer-filters-query";
import { adminFormResponsesApi } from "@/modules/forms/presentation/api/client";
import { useFormDetailQuery } from "@/modules/forms/presentation/api/queries";
import { AdminAddResponseDialog } from "@/modules/forms/presentation/ui/builder/AdminAddResponseDialog";
import { FormStatusBadge } from "@/modules/forms/presentation/ui/FormStatusBadge";
import { FormResponsesPanel } from "@/modules/forms/presentation/ui/responses/FormResponsesPanel";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";

export function FormResponsesPage() {
  const nav = useNavigate({ from: "/app/forms/$id/responses" });
  const { id } = useParams({ from: "/app/forms/$id/responses" });
  const search = useSearch({ from: "/app/forms/$id/responses" });
  const [addResponseOpen, setAddResponseOpen] = useState(false);

  const canUpdate = useActionPermission("forms:update");
  const detail = useFormDetailQuery(id);
  const form = detail.data?.form;
  const fields = detail.data?.fields ?? [];
  const questionCount = fields.filter((f) => f.type !== "section").length;
  const activeFilterCount = Object.keys(
    parseFiltSearchParam(search.filt),
  ).length;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຟອມ", to: "/app/forms/dashboard" },
            { label: "ຟອມ", to: "/app/forms" },
            ...(form
              ? [
                  {
                    label: form.title,
                    to: "/app/forms/$id/edit",
                    params: { id },
                  },
                ]
              : []),
            { label: "ຜົນຕອບ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-semibold text-2xl tracking-tight">
                {form?.title ?? "ຜົນຕອບຟອມ"}
              </h1>
              {form ? <FormStatusBadge status={form.status} /> : null}
            </div>
            {form ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{questionCount} ຄຳຖາມ</Badge>
                {activeFilterCount > 0 ? (
                  <Badge variant="secondary">
                    ກອງ {activeFilterCount} ຕົວ
                  </Badge>
                ) : null}
              </div>
            ) : null}
            <p className="text-muted-foreground text-sm">
              ຕາຕະລາງ · ສະຫຼຸບ · ສົ່ງອອກ CSV · ພິມ PDF
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdate && form ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAddResponseOpen(true)}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ເພີ່ມຄຳຕອບ
              </Button>
            ) : null}
            {form ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() =>
                  nav({ to: "/app/forms/$id/edit", params: { id } })
                }
              >
                <PencilIcon className="mr-2 h-4 w-4" />
                ແກ້ໄຂຟອມ
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => nav({ to: "/app/forms" })}
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບລາຍການ
            </Button>
          </div>
        </div>

        <QueryState
          result={detail}
          title="ກໍາລັງໂຫຼດຟອມ"
          description="ກຳລັງດຶງຂໍ້ມູນ"
          variant="fullscreen"
        >
          {!form ? null : (
            <>
              <FormResponsesPanel
                formId={id}
                formTitle={form.title}
                fields={fields}
                responsesApi={adminFormResponsesApi}
                hidePageHeader
                search={{
                  limit: search.limit,
                  offset: search.offset,
                  filt: search.filt,
                }}
                onNavigateSearch={(next) =>
                  nav({
                    search: {
                      ...search,
                      offset: next.offset,
                      limit: next.limit ?? search.limit,
                      filt: next.filt,
                    },
                  })
                }
              />
              <AdminAddResponseDialog
                formId={id}
                formTitle={form.title}
                fields={fields}
                open={addResponseOpen}
                onOpenChange={setAddResponseOpen}
              />
            </>
          )}
        </QueryState>
      </Main>
    </>
  );
}
